<script setup>
/**
 * RED-310 — "Ajavahemik" zone: five period modes (SelectButton — RadioButton
 * is not auto-imported here), from/to DatePickers for the free range, and
 * step + count for the multi-period comparison. Reports without period
 * comparison (RED-316 land) disable "Mitu ajavahemikku" and say why in the
 * same reserved hint line the range error uses.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalyticsStore } from '@/stores/analyticsStore'

const { t } = useI18n()
const store = useAnalyticsStore()

const modeOptions = computed(() =>
  ['range', 'previous_month', 'current_year', 'previous_year', 'multi'].map(value => ({
    value,
    label: t(`messages.transactions.analytics.form.period_${value}`),
    disabled: value === 'multi' && !store.multiAllowed,
  })),
)

const stepOptions = computed(() =>
  ['quarter', 'month', 'year'].map(value => ({
    value,
    label: t(`messages.transactions.analytics.form.step_${value}`),
  })),
)

const countOptions = [2, 3, 4, 5, 6, 7, 8].map(value => ({ value, label: String(value) }))

const isRange = computed(() => store.form.periodMode === 'range')
const isMulti = computed(() => store.form.periodMode === 'multi')
const rangeInvalid = computed(() => isRange.value && !store.rangeValid)
const hint = computed(() => {
  if (rangeInvalid.value) return t('messages.transactions.analytics.form.range_invalid')
  if (!store.multiAllowed) return t('messages.transactions.analytics.form.period_multi_unavailable')
  return ''
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-gray-700">
        {{ $t('messages.transactions.analytics.form.period') }}
      </label>
      <SelectButton
        :model-value="store.form.periodMode"
        :options="modeOptions"
        option-label="label"
        option-value="value"
        option-disabled="disabled"
        :allow-empty="false"
        size="small"
        class="w-auto! flex-wrap"
        data-testid="txn-analytics-period-mode"
        @update:model-value="store.setPeriodMode($event)"
      />
    </div>

    <div v-if="isRange" class="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-from" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.from') }}
        </label>
        <DatePicker
          v-model="store.form.from"
          input-id="txn-analytics-from"
          date-format="dd.mm.yy"
          show-icon
          icon-display="input"
          :max-date="store.form.to ?? undefined"
          :invalid="rangeInvalid"
          data-testid="txn-analytics-from"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-to" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.to') }}
        </label>
        <DatePicker
          v-model="store.form.to"
          input-id="txn-analytics-to"
          date-format="dd.mm.yy"
          show-icon
          icon-display="input"
          :min-date="store.form.from ?? undefined"
          :invalid="rangeInvalid"
          data-testid="txn-analytics-to"
        />
      </div>
    </div>

    <div v-else-if="isMulti" class="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-step" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.step') }}
        </label>
        <Select
          v-model="store.form.step"
          label-id="txn-analytics-step"
          :options="stepOptions"
          option-label="label"
          option-value="value"
          data-testid="txn-analytics-step"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-count" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.count') }}
        </label>
        <Select
          v-model="store.form.count"
          label-id="txn-analytics-count"
          :options="countOptions"
          option-label="label"
          option-value="value"
          data-testid="txn-analytics-count"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label for="txn-analytics-anchor" class="text-sm font-medium text-gray-700">
          {{ $t('messages.transactions.analytics.form.anchor') }}
        </label>
        <DatePicker
          v-model="store.form.to"
          input-id="txn-analytics-anchor"
          date-format="dd.mm.yy"
          show-icon
          icon-display="input"
          data-testid="txn-analytics-anchor"
        />
      </div>
    </div>

    <p
      class="text-xs text-gray-500 min-h-4"
      :class="{ invisible: !hint }"
      data-testid="txn-analytics-period-hint"
    >
      {{ hint }}
    </p>
  </div>
</template>
